import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import { Check, ArrowLeft, Users2, Clock3 } from "lucide-react";
import { PriceBreakdown } from "@/components/PriceIVA";

/**
 * Multi-service booking flow. Renders a 4-step flow:
 * 1. Pick multiple compatible services.
 * 2. Pick professional per service.
 * 3. Pick date and get suggested schedule.
 * 4. Fill client details + confirm.
 * Uses POST /t/{slug}/appointments/multi-search and /multi.
 */
export default function MultiServiceFlow({ services, onBackToSingle, user }) {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [step, setStep] = useState(1);
  const [pickedIds, setPickedIds] = useState([]); // array of service ids in order picked
  const [staffMap, setStaffMap] = useState({}); // service_id -> [staff]
  const [pickedStaff, setPickedStaff] = useState({}); // service_id -> staff_id
  const [date, setDate] = useState();
  const [options, setOptions] = useState(null); // null = untouched; [] = empty result; [...] = options
  const [pickedIdx, setPickedIdx] = useState(0);
  const [searching, setSearching] = useState(false);
  const [client, setClient] = useState({ name: user?.name || "", email: user?.email || "", phone: user?.phone || "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(null);

  // Only services that permit overlap are eligible in multi mode.
  const eligibleServices = useMemo(() => services.filter((s) => s.overlap_enabled), [services]);

  // Compat filter: a service is pickable if it is compatible with EVERY currently picked service.
  const pickableServices = useMemo(() => {
    if (pickedIds.length === 0) return eligibleServices;
    return eligibleServices.filter((s) => {
      if (pickedIds.includes(s.id)) return true; // already picked, still shown
      const mine = new Set(s.compatible_service_ids || []);
      return pickedIds.every((pid) => {
        const p = services.find((x) => x.id === pid);
        const theirs = new Set(p?.compatible_service_ids || []);
        return mine.has(pid) && theirs.has(s.id);
      });
    });
  }, [eligibleServices, pickedIds, services]);

  function toggleService(id) {
    setPickedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    setPickedStaff((prev) => {
      const n = { ...prev };
      if (n[id] !== undefined) delete n[id];
      return n;
    });
    setOptions(null);
    setPickedIdx(0);
  }

  // Any picked service requires payment? we don't support deposit consolidation yet.
  const requiresPayment = useMemo(
    () => pickedIds.some((id) => services.find((s) => s.id === id)?.requires_payment),
    [pickedIds, services]
  );

  const totalPrice = useMemo(
    () => pickedIds.reduce((sum, id) => sum + (services.find((s) => s.id === id)?.price || 0), 0),
    [pickedIds, services]
  );

  // Load staff list for each picked service
  useEffect(() => {
    async function loadStaff() {
      const map = { ...staffMap };
      for (const sid of pickedIds) {
        if (!map[sid]) {
          try {
            const r = await api.get(`/t/${slug}/staff/by-service/${sid}`);
            map[sid] = r.data || [];
          } catch { map[sid] = []; }
        }
      }
      setStaffMap(map);
    }
    if (pickedIds.length) loadStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedIds, slug]);

  async function searchSchedule() {
    if (!date || pickedIds.length < 2) return;
    setSearching(true);
    setOptions(null);
    try {
      const items = pickedIds.map((id) => ({ service_id: id, staff_id: pickedStaff[id] }));
      const { data } = await api.post(`/t/${slug}/appointments/multi-search`, {
        date: format(date, "yyyy-MM-dd"),
        items,
      });
      setOptions(data.options || []);
      setPickedIdx(0);
    } catch (e) {
      toast.error(formatApiError(e, "No hay horario disponible"));
      setOptions([]);
    } finally { setSearching(false); }
  }

  // Auto-refresh when picking date at step 3
  useEffect(() => {
    if (step === 3 && date && pickedIds.length >= 2 && pickedIds.every((id) => pickedStaff[id])) {
      searchSchedule();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, step]);

  // Disabled days for the calendar: past + weekdays where at least ONE picked staff doesn't work
  const DAYS_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"]; // JS getDay index
  const disabledDayFn = React.useCallback((d) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (d < today) return true;
    const key = DAYS_KEYS[d.getDay()];
    for (const sid of pickedIds) {
      const staff = (staffMap[sid] || []).find((s) => s.id === pickedStaff[sid]);
      if (!staff) return true; // safety
      const cfg = (staff.schedule || {})[key];
      if (!cfg || !cfg.enabled) return true;
      // time_off blocks
      for (const off of staff.time_off || []) {
        try {
          const start = new Date(off.start_date);
          const end = new Date(off.end_date);
          const cur = new Date(d.getFullYear(), d.getMonth(), d.getDate());
          if (cur >= start && cur <= end) return true;
        } catch { /* ignore */ }
      }
    }
    return false;
  }, [pickedIds, pickedStaff, staffMap]);

  async function submit() {
    const chosen = options?.[pickedIdx];
    if (!chosen || !date) return;
    if (!client.name.trim()) { toast.error("Ingresa tu nombre"); return; }
    setLoading(true);
    try {
      const payload = {
        date: format(date, "yyyy-MM-dd"),
        client_name: client.name.trim(),
        client_email: (client.email || "").trim().toLowerCase(),
        client_phone: client.phone || "",
        notes: client.notes || "",
        schedule: chosen.schedule.map((s) => ({ service_id: s.service_id, staff_id: s.staff_id, time: s.time })),
      };
      const { data } = await api.post(`/t/${slug}/appointments/multi`, payload);
      setCreated({ items: data, schedule: chosen, total: totalPrice });
    } catch (e) {
      toast.error(formatApiError(e, "No se pudo agendar"));
    } finally { setLoading(false); }
  }

  if (created) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-6 sm:p-8 space-y-4">
          <div className="flex flex-col items-center text-center gap-2">
            <div className="h-14 w-14 rounded-full bg-secondary text-secondary-foreground grid place-items-center"><Check className="h-7 w-7" /></div>
            <h2 className="font-display text-2xl mt-2">¡Visita agendada!</h2>
            <p className="text-slate-600">Recibirás una confirmación por correo si nos diste tu email.</p>
          </div>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr><th className="py-2 px-3 text-left">Hora</th><th className="py-2 px-3 text-left">Servicio</th><th className="py-2 px-3 text-left">Profesional</th></tr>
              </thead>
              <tbody>
                {created.schedule.schedule.map((s, i) => (
                  <tr key={`${s.service_id}-${s.time}-${i}`} className="border-t border-slate-200">
                    <td className="py-2 px-3 font-medium">{s.time}</td>
                    <td className="py-2 px-3">{s.service_name}</td>
                    <td className="py-2 px-3 text-slate-500">{staffMap[s.service_id]?.find((x) => x.id === s.staff_id)?.name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PriceBreakdown total={created.total} ivaIncluded={true} />
          <Button className="w-full" onClick={() => navigate(`/${slug}`)}>Volver</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="multi-flow">
      {/* Step 1: multi-select services */}
      {step === 1 && (
        <Card className="border-slate-200">
          <CardContent className="p-6 sm:p-8">
            <p className="overline">01 · Servicios</p>
            <h2 className="font-display text-2xl mt-2 mb-2">Elige los servicios de tu visita</h2>
            <p className="text-sm text-slate-500 mb-4">Solo aparecen servicios compatibles entre sí.</p>
            {eligibleServices.length === 0 && (
              <div className="rounded-md border border-dashed border-slate-300 p-4 text-sm text-slate-600" data-testid="multi-no-eligible">
                No hay servicios configurados para empalme todavía. Regresa al modo de un solo servicio.
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              {pickableServices.map((s) => {
                const picked = pickedIds.includes(s.id);
                return (
                  <button
                    key={s.id}
                    onClick={() => toggleService(s.id)}
                    className={`text-left rounded-lg border p-5 card-hover transition-all duration-200 ${picked ? "border-primary bg-secondary ring-2 ring-primary/30" : "border-slate-200 bg-white"}`}
                    data-testid={`multi-service-${s.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-display text-lg">{s.name}</p>
                        <p className="text-sm text-slate-600 mt-1">{s.description}</p>
                      </div>
                      {picked && <Check className="h-5 w-5 text-primary" />}
                    </div>
                    <div className="mt-4 flex items-center gap-4 text-sm text-slate-500">
                      <span>{s.duration_min} min</span>
                      <span>${s.price.toFixed(2)}</span>
                    </div>
                  </button>
                );
              })}
            </div>
            {requiresPayment && pickedIds.length > 0 && (
              <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-xs p-3" data-testid="multi-payment-warning">
                ⚠️ Uno o más servicios seleccionados requieren depósito. El pago consolidado en modo multi-servicio no está disponible aún. Quita esos servicios o usa el modo tradicional.
              </div>
            )}
            <div className="mt-6 flex justify-between">
              <Button variant="outline" onClick={onBackToSingle} data-testid="multi-back-to-single"><ArrowLeft className="h-4 w-4 mr-2" /> Modo un servicio</Button>
              <Button disabled={pickedIds.length < 2 || requiresPayment} onClick={() => setStep(2)} data-testid="multi-step1-next">Continuar ({pickedIds.length})</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: pick professional per service */}
      {step === 2 && (
        <Card className="border-slate-200">
          <CardContent className="p-6 sm:p-8 space-y-6">
            <p className="overline">02 · Profesionales</p>
            <h2 className="font-display text-2xl">Elige profesional para cada servicio</h2>
            {pickedIds.map((sid) => {
              const svc = services.find((s) => s.id === sid);
              const staff = staffMap[sid] || [];
              return (
                <div key={sid} className="border border-slate-200 rounded-lg p-4">
                  <p className="font-display text-base">{svc?.name}</p>
                  <p className="text-xs text-slate-500 mb-3">{svc?.duration_min} min</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {staff.length === 0 && <p className="text-xs text-slate-400 col-span-2">Sin profesionales asignados a este servicio.</p>}
                    {staff.map((st) => {
                      const active = pickedStaff[sid] === st.id;
                      return (
                        <button
                          key={st.id}
                          onClick={() => setPickedStaff({ ...pickedStaff, [sid]: st.id })}
                          className={`text-left rounded border p-3 card-hover transition-all ${active ? "border-primary bg-secondary" : "border-slate-200"}`}
                          data-testid={`multi-staff-${sid}-${st.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-full bg-secondary text-secondary-foreground grid place-items-center text-xs">{st.name?.[0] || "·"}</div>
                            <div>
                              <p className="text-sm font-medium">{st.name}</p>
                              <p className="text-xs text-slate-500">{st.title}</p>
                            </div>
                            {active && <Check className="h-4 w-4 text-primary ml-auto" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>Atrás</Button>
              <Button
                disabled={pickedIds.some((id) => !pickedStaff[id])}
                onClick={() => setStep(3)}
                data-testid="multi-step2-next"
              >Continuar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: pick date + search schedule */}
      {step === 3 && (
        <Card className="border-slate-200">
          <CardContent className="p-6 sm:p-8 space-y-6">
            <p className="overline">03 · Fecha y horario</p>
            <h2 className="font-display text-2xl">Selecciona la fecha y una opción de horario</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => { setDate(d); setOptions(null); setPickedIdx(0); }}
                  locale={es}
                  disabled={disabledDayFn}
                  data-testid="multi-calendar"
                />
                <Button className="mt-4" disabled={!date || searching} onClick={searchSchedule} data-testid="multi-search-btn">
                  {searching ? "Buscando…" : "Refrescar opciones"}
                </Button>
                <p className="text-[11px] text-slate-500 mt-2">Los días en gris no están disponibles porque al menos uno de los profesionales elegidos no atiende ese día.</p>
              </div>
              <div>
                {searching && (
                  <div className="rounded-md border border-dashed border-slate-300 p-6 text-sm text-slate-500 text-center">
                    Buscando opciones disponibles…
                  </div>
                )}
                {!searching && options === null && (
                  <div className="rounded-md border border-dashed border-slate-300 p-6 text-sm text-slate-500 text-center">
                    Elige una fecha para ver opciones de horario.
                  </div>
                )}
                {!searching && Array.isArray(options) && options.length === 0 && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 text-center" data-testid="multi-no-options">
                    No hay horarios disponibles ese día para la combinación de profesionales elegida. Prueba otra fecha.
                  </div>
                )}
                {!searching && Array.isArray(options) && options.length > 0 && (
                  <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="multi-options-list">
                    <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">{options.length} opción{options.length === 1 ? "" : "es"} disponible{options.length === 1 ? "" : "s"}</p>
                    {options.map((opt, idx) => {
                      const active = idx === pickedIdx;
                      return (
                        <button
                          key={`${opt.mode}-${opt.start}-${idx}`}
                          onClick={() => setPickedIdx(idx)}
                          className={`w-full text-left rounded-lg border p-3 transition-all ${active ? "border-primary bg-secondary ring-2 ring-primary/30" : "border-slate-200 bg-white hover:bg-slate-50"}`}
                          data-testid={`multi-option-${idx}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <Clock3 className="h-4 w-4 text-primary" />
                              <span className="font-medium text-sm">{opt.start} – {opt.end}</span>
                              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${opt.mode === "parallel" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
                                {opt.mode === "parallel" ? "En paralelo" : "Secuencial"}
                              </span>
                            </div>
                            {active && <Check className="h-4 w-4 text-primary" />}
                          </div>
                          <div className="text-xs text-slate-500 space-y-0.5 pl-6">
                            {opt.schedule.map((s, i) => (
                              <div key={`${s.service_id}-${s.time}-${i}`} className="flex gap-2">
                                <span className="font-medium text-slate-600 w-11">{s.time}</span>
                                <span className="flex-1 truncate">{s.service_name}</span>
                                <span className="text-slate-400 truncate max-w-[8rem]">{staffMap[s.service_id]?.find((x) => x.id === s.staff_id)?.name || "—"}</span>
                              </div>
                            ))}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)}>Atrás</Button>
              <Button disabled={!options || options.length === 0} onClick={() => setStep(4)} data-testid="multi-step3-next">Continuar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: client details + submit */}
      {step === 4 && (
        <Card className="border-slate-200">
          <CardContent className="p-6 sm:p-8 space-y-4">
            <p className="overline">04 · Tus datos</p>
            <h2 className="font-display text-2xl">Confirma tu visita</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Nombre*</Label>
                <Input value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} data-testid="multi-client-name" />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })} data-testid="multi-client-phone" />
              </div>
              <div className="sm:col-span-2">
                <Label>Email (opcional)</Label>
                <Input type="email" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} data-testid="multi-client-email" />
              </div>
              <div className="sm:col-span-2">
                <Label>Notas</Label>
                <Textarea rows={2} value={client.notes} onChange={(e) => setClient({ ...client, notes: e.target.value })} data-testid="multi-client-notes" />
              </div>
            </div>
            <div className="border border-slate-200 rounded-lg p-4">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Resumen</p>
              <div className="text-sm space-y-1">
                {options?.[pickedIdx]?.schedule.map((s, i) => (
                  <div key={`${s.service_id}-${s.time}-${i}`} className="flex justify-between">
                    <span>{s.time} · {s.service_name}</span>
                    <span>${(services.find((x) => x.id === s.service_id)?.price || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-200 mt-2 pt-2">
                <PriceBreakdown total={totalPrice} ivaIncluded={true} />
              </div>
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(3)}>Atrás</Button>
              <Button disabled={loading || !client.name.trim()} onClick={submit} data-testid="multi-submit">
                {loading ? "Agendando…" : "Confirmar visita"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
